/* Self-signup, email verification, password reset/change, invitations, notification preferences.
 * Mirrors api/internal/api/h_account.go (validation, messages, status codes, token rules, rate limits). */
import { trialEnd } from "../../domain/compliance";
import { ApiError } from "../provider";
import type * as T from "../types";
import { isCountryCode } from "../types";
import { countryOf, siDefaults, validCrFor, validVatFor } from "../../domain/country";
import { crMessage, vatMessage } from "./countryRules";
import { isBusinessCategory } from "../types";
import { E, randomHex, str, type Ctx } from "./ctx";
import { preferenceKeys, userRecipient, validAddress } from "./mailer";
import { createTenantState, PLATFORM, type AuthTokenRec } from "./state";

export const VERIFY_TTL_MS = 48 * 3600_000;
export const RESET_TTL_MS = 60 * 60_000;
export const INVITE_TTL_MS = 7 * 24 * 3600_000;

/** Password policy (same as Go passwordProblem). */
export function passwordProblem(pw: string): string {
  if ([...pw].length < 8) return "must be at least 8 characters";
  if (pw.length > 128) return "must be at most 128 characters";
  if (!/\p{L}/u.test(pw) || !/\p{N}/u.test(pw)) return "must contain letters and digits";
  return "";
}

/** Sliding-window limiter (per MockProvider; the mock has one "client IP"). */
export function rateLimit(c: Ctx, key: string, limit: number, windowMs: number) {
  const now = c.wallNow();
  const hits = (c.root.rateHits[key] ?? []).filter((t) => now - t < windowMs);
  if (hits.length >= limit) {
    c.root.rateHits[key] = hits;
    const secs = Math.ceil((windowMs - (now - hits[0])) / 1000) + 1;
    throw E.rateLimited(`too many requests — try again in ${Math.ceil(secs / 60)} min`);
  }
  hits.push(now);
  c.root.rateHits[key] = hits;
}

const IP = "local";
const normEmail = (e: unknown) => str(e).trim().toLowerCase();
const badToken = () => E.field("token", "invalid or expired link");

export function accountOps(c: Ctx) {
  const newToken = (u: T.User, purpose: AuthTokenRec["purpose"], ttl: number): string => {
    c.root.authTokens = c.root.authTokens.filter((t) => !(t.userId === u.id && t.purpose === purpose));
    const token = randomHex(32);
    c.root.authTokens.push({ token, purpose, userId: u.id, email: u.email, expiresAt: c.wallNow() + ttl, usedAt: null });
    return token;
  };
  const consume = (tok: unknown, ...purposes: AuthTokenRec["purpose"][]): { rec: AuthTokenRec; user: T.User } => {
    const t = str(tok).trim();
    if (t.length !== 64) throw badToken();
    const rec = c.root.authTokens.find((x) => x.token === t);
    if (!rec || !purposes.includes(rec.purpose) || rec.usedAt != null || c.wallNow() >= rec.expiresAt) throw badToken();
    const user = c.root.users.find((u) => u.id === rec.userId);
    if (!user || user.email.toLowerCase() !== rec.email.toLowerCase()) throw badToken();
    rec.usedAt = c.wallNow();
    return { rec, user };
  };
  const respond = (u: T.User): T.LoginResponse => {
    const token = c.issueToken(u);
    c.token = token;
    return { token, user: c.publicUser(u) };
  };
  const auditAs = (u: T.User, action: string, entity: string, id: string, detail: string) => {
    const prev = c.state;
    c.use(u.tenantId === PLATFORM ? c.root.tenantId : u.tenantId);
    c.audit(u, action, entity, id, detail);
    c.state = prev;
  };
  const sendVerification = (u: T.User) => {
    const tok = newToken(u, "verify", VERIFY_TTL_MS);
    c.mail.notify(u.tenantId, "verify-email", userRecipient(c, u), { Link: "/#/verify-email?token=" + tok, Hours: "48" });
  };
  const setPassword = (u: T.User, pw: string) => {
    c.root.passwords[u.id] = pw;
    c.root.unverified.delete(u.id);
    c.root.tokenVersions[u.id] = (c.root.tokenVersions[u.id] ?? 0) + 1;
  };
  const passwordChangedMail = (u: T.User) => {
    const when = new Date(c.wallNow() + 3 * 3600_000).toISOString().slice(0, 16).replace("T", " ");
    c.mail.notify(u.tenantId, "password-changed", userRecipient(c, u), { When: when, Link: "/#/forgot-password" });
  };

  return {
    async signup(req: T.SignupRequest): Promise<T.SignupResponse> {
      const companyName = str(req?.companyName).trim(), companyNameAr = str(req?.companyNameAr).trim(), ownerName = str(req?.ownerName).trim(), city = str(req?.city).trim();
      const cr = str(req?.cr).replace(/ /g, ""), vat = str(req?.vat).replace(/ /g, ""), email = normEmail(req?.email), password = str(req?.password);
      const plan = str(req?.plan) || "Business", lang = str(req?.lang) || "en";
      const fe: Record<string, string> = {};
      if (!companyName) fe.companyName = "required"; else if ([...companyName].length > 120) fe.companyName = "max 120 characters";
      if ([...companyNameAr].length > 120) fe.companyNameAr = "max 120 characters";
      const country = str(req?.country).trim().toUpperCase() || "SA";
      if (!isCountryCode(country)) fe.country = "unknown country";
      else {
        if (!validCrFor(cr, country)) fe.cr = crMessage(country);
        if (vat && !validVatFor(vat, country)) fe.vat = vatMessage(country);
      }
      if (!city) fe.city = "required";
      const category = str(req?.category).trim();
      if (category && !isBusinessCategory(category)) fe.category = "unknown category";
      if (!ownerName) fe.ownerName = "required"; else if ([...ownerName].length > 100) fe.ownerName = "max 100 characters";
      if (!validAddress(email)) fe.email = "a valid email address is required";
      const pp = passwordProblem(password); if (pp) fe.password = pp;
      if (!(plan in c.root.planPrices)) fe.plan = "unknown plan";
      if (lang !== "en" && lang !== "ar") fe.lang = "must be en or ar";
      if (Object.keys(fe).length) throw E.validation(fe);
      rateLimit(c, "signup|ip|" + IP, 5, 3600_000);
      if (c.root.users.some((u) => u.email.toLowerCase() === email)) throw new ApiError(409, "conflict", "this email is already registered — sign in or reset your password", { email: "already registered" });
      if (c.root.tenants.some((t) => t.cr === cr)) throw new ApiError(409, "conflict", "a company with this CR number is already registered", { cr: "already registered" });
      const tid = "t-" + randomHex(6), uid = "u-" + randomHex(6);
      const tenant: T.Tenant = { id: tid, name: companyName, nameAr: companyNameAr, cr, vat, city, category: category as T.BusinessCategory | "", country: country as T.CountryCode };
      const st = createTenantState(c.root, tenant, c.today, plan);
      if (country !== "SA") {
        st.settings.gosi = siDefaults(country);
        st.settings.attendance = { ...st.settings.attendance, weekendDays: [...countryOf(country).weekend] };
      }
      c.tenantStates.set(tid, st);
      c.root.tenants.push({ id: tid, name: companyName, cr, city, plan, seats: 10, status: "Trial", since: c.today, aiCredits: 0, category: tenant.category, country: tenant.country });
      const user: T.User = { id: uid, email, name: ownerName, nameAr: ownerName, role: "owner", tenantId: tid, employeeId: null, lang: lang as T.Lang };
      c.root.users.push(user);
      c.root.passwords[uid] = password;
      c.root.unverified.add(uid);
      sendVerification(user);
      auditAs(user, "Registered company", "Tenant", tid, `${companyName} · ${plan} trial`);
      c.audit({ ...user, name: "StartHR", tenantId: PLATFORM }, "Company signed up", "Tenant", tid, `${companyName} (CR ${cr})`);
      return { pendingVerification: true, email };
    },

    async verifyEmail(token: string): Promise<T.LoginResponse> {
      rateLimit(c, "verify|ip|" + IP, 30, 15 * 60_000);
      const { user } = consume(token, "verify");
      c.root.unverified.delete(user.id);
      const st = c.stateFor(user.tenantId);
      if (st && user.role === "owner") {
        const sub = st.subscription;
        const end = trialEnd(sub);
        const rc = userRecipient(c, user);
        c.mail.notify(user.tenantId, "welcome", rc, { Link: "/#/dashboard", Plan: sub.plan, TrialEnd: end }, "welcome:" + user.id);
        c.mail.notify(user.tenantId, "trial-started", rc, { Link: "/#/billing", Plan: sub.plan, TrialEnd: end, Days: String(sub.trialDays) }, "trial-started:" + user.tenantId);
      }
      return respond(user);
    },

    async resendVerification(email: string): Promise<void> {
      const em = normEmail(email);
      if (!validAddress(em)) throw E.field("email", "a valid email address is required");
      rateLimit(c, "resend|email|" + em, 3, 15 * 60_000);
      rateLimit(c, "resend|ip|" + IP, 10, 15 * 60_000);
      const u = c.root.users.find((x) => x.email.toLowerCase() === em);
      if (u && c.root.unverified.has(u.id)) sendVerification(u);
    },

    async forgotPassword(email: string): Promise<void> {
      const em = normEmail(email);
      if (!validAddress(em)) throw E.field("email", "a valid email address is required");
      rateLimit(c, "forgot|email|" + em, 5, 15 * 60_000);
      rateLimit(c, "forgot|ip|" + IP, 20, 15 * 60_000);
      const u = c.root.users.find((x) => x.email.toLowerCase() === em);
      if (!u) return;
      const tok = newToken(u, "reset", RESET_TTL_MS);
      c.mail.notify(u.tenantId, "password-reset", userRecipient(c, u), { Link: "/#/reset-password?token=" + tok, Minutes: "60" });
    },

    async resetPassword(token: string, password: string): Promise<{ email: string }> {
      const pp = passwordProblem(str(password));
      if (pp) throw E.field("password", pp);
      rateLimit(c, "reset|ip|" + IP, 30, 15 * 60_000);
      const { rec, user } = consume(token, "reset", "invite");
      setPassword(user, password);
      c.root.authTokens = c.root.authTokens.filter((t) => !(t.userId === user.id && t.purpose === "reset"));
      if (rec.purpose === "reset") passwordChangedMail(user);
      auditAs(user, rec.purpose === "invite" ? "Accepted invitation" : "Reset password", "User", user.id, "");
      return { email: user.email };
    },

    async changePassword(current: string, next: string): Promise<T.LoginResponse> {
      const u = c.principal("authenticated");
      const cur = str(current), nw = str(next);
      const fe: Record<string, string> = {};
      if (!cur) fe.current = "required";
      const pp = passwordProblem(nw);
      if (pp) fe.new = pp; else if (nw === cur) fe.new = "must differ from the current password";
      if (Object.keys(fe).length) throw E.validation(fe);
      rateLimit(c, "chpw|" + u.id, 10, 15 * 60_000);
      if (cur !== (c.root.passwords[u.id] ?? c.root.demoPassword)) throw E.field("current", "incorrect password");
      setPassword(u, nw);
      passwordChangedMail(u);
      c.audit(u, "Changed password", "User", u.id, "");
      return respond(u);
    },

    async updateMe(patch: { lang: T.Lang }): Promise<T.User> {
      const u = c.principal("authenticated");
      const lang = str(patch?.lang);
      if (lang !== "en" && lang !== "ar") throw E.field("lang", "must be en or ar");
      const stored = c.root.users.find((x) => x.id === u.id)!;
      stored.lang = lang;
      return stored;
    },

    async inviteEmployee(id: number): Promise<T.InviteResult> {
      const me = c.principal("employees.write");
      const e = c.state.employees.find((x) => x.id === id);
      if (!e) throw E.notFound("employee not found");
      const em = e.email.trim().toLowerCase();
      if (!validAddress(em)) throw E.field("email", "add a valid email address to the employee first");
      if (e.status === "Terminated") throw E.conflict("employee is terminated");
      let u = c.root.users.find((x) => x.tenantId === c.tid && x.employeeId === id);
      if (u) {
        if (!c.root.unverified.has(u.id)) throw E.conflict("employee already has an active account");
        if (u.email.toLowerCase() !== em) {
          if (c.root.users.some((x) => x.email.toLowerCase() === em)) throw E.conflict("this email is already registered");
          u.email = em;
        }
      } else {
        if (c.root.users.some((x) => x.email.toLowerCase() === em)) throw E.conflict("this email is already registered");
        u = { id: "u-" + randomHex(6), email: em, name: e.nameEn, nameAr: e.nameAr, role: "employee", tenantId: c.tid, employeeId: id };
        c.root.users.push(u);
        c.root.passwords[u.id] = randomHex(24);
        c.root.unverified.add(u.id);
      }
      const tok = newToken(u, "invite", INVITE_TTL_MS);
      c.mail.notify(c.tid, "user-invite", { email: em, name: e.nameEn.split(" ")[0], lang: u.lang ?? "" },
        { Inviter: me.name, Link: "/#/reset-password?token=" + tok + "&invite=1", Days: "7" });
      c.audit(me, "Invited employee to self-service", "Employee", String(id), em);
      return { ok: true, userId: u.id, email: em };
    },

    async getNotificationPrefs(): Promise<T.NotificationPref[]> {
      c.principal("settings.write");
      return preferenceKeys().map(([key, group]) => ({ key, group, enabled: !c.state.notifPrefs[key] }));
    },

    async updateNotificationPrefs(patch: Record<string, boolean>): Promise<T.NotificationPref[]> {
      const u = c.principal("settings.write");
      if (patch == null || typeof patch !== "object" || Array.isArray(patch)) throw E.badRequest("JSON object expected");
      const known = new Set(preferenceKeys().map(([k]) => k));
      const fe: Record<string, string> = {};
      for (const [k, v] of Object.entries(patch)) {
        if (!known.has(k)) fe[k] = "unknown notification";
        else if (typeof v !== "boolean") fe[k] = "must be true or false";
      }
      if (Object.keys(fe).length) throw E.validation(fe);
      const changed: string[] = [];
      for (const [k, on] of Object.entries(patch)) {
        if (!!c.state.notifPrefs[k] === on) changed.push(`${k}=${on}`);
        if (on) delete c.state.notifPrefs[k]; else c.state.notifPrefs[k] = true;
      }
      c.audit(u, "Updated notification preferences", "Settings", c.tid, changed.sort().join(", "));
      return preferenceKeys().map(([key, group]) => ({ key, group, enabled: !c.state.notifPrefs[key] }));
    },
  };
}
